/**
 * Step 2 — 파일 소스 설정
 *
 * 로컬 파일 업로드 / Google Drive 파일 가져오기 탭
 * KB용 Google Drive는 워크플로우와 별도로 관리 (kb-google-drive)
 */

import React, { useRef, useCallback, useState, useEffect } from 'react';
import connectorApi from '../../../api/connectors';
import { Badge, Button, Checkbox, Notice } from '../../ui';
import { KbGoogleDriveConfigModal } from '../KbGoogleDriveConfigModal';
import { KbDropboxConfigModal } from '../KbDropboxConfigModal';
import googleDriveLogo from '../../../assets/icons/marketplace/google-drive.svg';
import dropboxLogo from '../../../assets/icons/marketplace/dropbox.svg';
import LocalIcon from '../../../assets/icons/common/computer.svg?react';
import RefreshIcon from '../../../assets/icons/common/refresh.svg?react';
import SpreadsheetIcon from '../../../assets/icons/common/spreadsheet.svg';
import PptIcon from '../../../assets/icons/common/presentation.svg';
import PdfIcon from '../../../assets/icons/common/pdf.svg';
import folderIcon from '../../../assets/icons/common/folder_color.svg';
import ImageIcon from '../../../assets/icons/common/image.svg';
import DocumentIcon from '../../../assets/icons/common/document.svg';

const ACCEPTED_MIME = [
  'application/pdf',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'image/png',
  'image/jpeg',
];

function formatFileSize(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function isValidFile(f: File): boolean {
  return (
    ACCEPTED_MIME.includes(f.type) ||
    f.name.endsWith('.md') ||
    f.name.endsWith('.xlsx') ||
    f.name.endsWith('.xls') ||
    f.name.endsWith('.png') ||
    f.name.endsWith('.jpg') ||
    f.name.endsWith('.jpeg')
  );
}

interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: number;
  modifiedTime?: string;
  webViewLink?: string;
}

export interface DriveFileSelection {
  fileId: string;
  fileName: string;
}

export interface DropboxFileSelection {
  filePath: string;
  fileName: string;
}

interface FileSourceStepProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  isDragOver: boolean;
  onIsDragOverChange: (v: boolean) => void;
  // Google Drive 선택 파일 목록
  driveFiles?: DriveFileSelection[];
  onDriveFilesChange?: (files: DriveFileSelection[]) => void;
  // Dropbox 선택 파일 목록
  dropboxFiles?: DropboxFileSelection[];
  onDropboxFilesChange?: (files: DropboxFileSelection[]) => void;
}

export function FileSourceStep({
  files,
  onFilesChange,
  isDragOver,
  onIsDragOverChange,
  driveFiles = [],
  onDriveFilesChange,
  dropboxFiles = [],
  onDropboxFilesChange,
}: FileSourceStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // 로컬/Drive/Dropbox 탭 전환
  const [activeTab, setActiveTab] = useState<'local' | 'drive' | 'dropbox'>('local');
  // 지원하지 않는 파일 에러 상태
  const [fileError, setFileError] = useState<string>('');

  // Google Drive 연결 상태
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveAccount, setDriveAccount] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(false);

  // 파일 목록 조회
  const [folderId, setFolderId] = useState('');
  const [driveFileList, setDriveFileList] = useState<DriveFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [driveError, setDriveError] = useState('');

  // 설정 모달
  const [showConfigModal, setShowConfigModal] = useState(false);

  // Dropbox 연결 상태
  const [dropboxConnected, setDropboxConnected] = useState(false);
  const [dropboxAccount, setDropboxAccount] = useState('');
  const [checkingDropbox, setCheckingDropbox] = useState(false);
  const [dropboxFolderPath, setDropboxFolderPath] = useState('');
  const [dropboxFileList, setDropboxFileList] = useState<Array<{ id: string; name: string; path_lower: string; '.tag': string; size?: number; server_modified?: string }>>([]);
  const [loadingDropboxFiles, setLoadingDropboxFiles] = useState(false);
  const [dropboxError, setDropboxError] = useState('');
  const [showDropboxConfigModal, setShowDropboxConfigModal] = useState(false);

  const handleTabChange = (tab: 'local' | 'drive' | 'dropbox') => {
    if (tab === 'drive') {
      onFilesChange([]);
      onDropboxFilesChange?.([]);
    } else if (tab === 'dropbox') {
      onFilesChange([]);
      onDriveFilesChange?.([]);
    } else {
      onDriveFilesChange?.([]);
      onDropboxFilesChange?.([]);
    }
    setActiveTab(tab);
  };

  // Drive 탭 진입 시
  useEffect(() => {
    if (activeTab !== 'drive') return;
    setCheckingStatus(true);
    connectorApi.getKbGoogleDriveConfig().then(cfg => {
      if (cfg?.connected) {
        setDriveConnected(true);
        setDriveAccount(cfg.connectedEmail || '');
      } else {
        setDriveConnected(false);
        setDriveAccount('');
      }
    }).catch(() => { setDriveConnected(false); }).finally(() => { setCheckingStatus(false); });
    setDriveFileList([]); setFolderId(''); setDriveError('');
  }, [activeTab]);

  // Dropbox 탭 진입 시
  useEffect(() => {
    if (activeTab !== 'dropbox') return;
    setCheckingDropbox(true);
    connectorApi.getKbDropboxConfig().then(cfg => {
      if (cfg?.connected) {
        setDropboxConnected(true);
        setDropboxAccount(cfg.connectedEmail || '');
      } else {
        setDropboxConnected(false);
        setDropboxAccount('');
      }
    }).catch(() => { setDropboxConnected(false); }).finally(() => { setCheckingDropbox(false); });
    setDropboxFileList([]); setDropboxFolderPath(''); setDropboxError('');
  }, [activeTab]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    onIsDragOverChange(true);
  }, [onIsDragOverChange]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    onIsDragOverChange(false);
  }, [onIsDragOverChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    onIsDragOverChange(false);
    const allFiles = Array.from(e.dataTransfer.files);
    const valid = allFiles.filter(isValidFile);
    const invalid = allFiles.filter(f => !isValidFile(f));
    
    if (invalid.length > 0) {
      const invalidNames = invalid.map(f => f.name).join(', ');
      setFileError(`지원하지 않는 파일 형식: ${invalidNames}`);
      setTimeout(() => setFileError(''), 3000);
    } else {
      setFileError('');
    }
    
    if (valid.length > 0) {
      onFilesChange([...files, ...valid]);
    }
  }, [files, onFilesChange, onIsDragOverChange]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilesChange([...files, ...Array.from(e.target.files || [])]);
    e.target.value = '';
  };

  const removeFile = (idx: number) => {
    onFilesChange(files.filter((_, i) => i !== idx));
  };

  // Drive 파일 목록 조회 (KB용)
  const loadDriveFiles = async () => {
    setLoadingFiles(true);
    setDriveError('');
    try {
      const list = await connectorApi.listKbGoogleDriveFiles(folderId || undefined);
      setDriveFileList(list);
    } catch {
      setDriveError('파일 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoadingFiles(false);
    }
  };

  // Dropbox 파일 목록 조회
  const loadDropboxFiles = async () => {
    setLoadingDropboxFiles(true);
    setDropboxError('');
    try {
      const list = await connectorApi.listKbDropboxFiles(dropboxFolderPath || undefined);
      setDropboxFileList(list);
    } catch {
      setDropboxError('파일 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoadingDropboxFiles(false);
    }
  };

  // Drive 파일 선택/해제 토글
  const toggleDriveFile = (file: DriveFile) => {
    if (!onDriveFilesChange) return;
    const already = driveFiles.some(f => f.fileId === file.id);
    if (already) {
      onDriveFilesChange(driveFiles.filter(f => f.fileId !== file.id));
    } else {
      onDriveFilesChange([...driveFiles, { fileId: file.id, fileName: file.name }]);
    }
  };

  // Google Drive mimeType → 표시 레이블
  // Drive 파일 종류 → 컬러 아이콘 (assets/icons/common)
  const getDriveFileTypeIcon = (mimeType: string) => {
    if (mimeType.includes('folder')) return folderIcon;
    if (mimeType.includes('spreadsheet')) return SpreadsheetIcon;
    if (mimeType.includes('presentation')) return PptIcon;
    if (mimeType.includes('pdf')) return PdfIcon;
    if (mimeType.includes('image')) return ImageIcon;
    return DocumentIcon; // document · 그 외
  };

  // 모달에서 연결 완료 시
  const handleConnected = (email: string) => {
    setDriveConnected(true);
    setDriveAccount(email);
  };

  return (
    <div className="pipe-tab-content">

      {/* 로컬 / Drive / Dropbox 탭 */}
      <div className="pipe-file-source-tabs">
        <button
          className={`pipe-file-source-tab${activeTab === 'local' ? ' active' : ''}`}
          onClick={() => handleTabChange('local')}
        >
          <LocalIcon /> 로컬 파일
        </button>
        <button
          className={`pipe-file-source-tab${activeTab === 'drive' ? ' active' : ''}`}
          onClick={() => handleTabChange('drive')}
        >
          <img src={googleDriveLogo} alt="" /> Google Drive
        </button>
        <button
          className={`pipe-file-source-tab${activeTab === 'dropbox' ? ' active' : ''}`}
          onClick={() => handleTabChange('dropbox')}
        >
          <img src={dropboxLogo} alt="" /> Dropbox
        </button>
      </div>

      {/* ── 로컬 파일 탭 ── */}
      {activeTab === 'local' && (
        <>
          <div
            className={`pipe-file-dropzone${isDragOver ? ' dragover' : ''}${fileError ? ' has-error' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="pipe-file-dropzone-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                <polyline points="13 2 13 9 20 9" />
              </svg>
            </div>
            <div className="pipe-file-dropzone-text">파일을 드래그하거나 클릭해서 선택</div>
            <div className="pipe-file-dropzone-sub">PDF, TXT, MD, Excel, 이미지(PNG/JPG) (최대 50MB)</div>
            {fileError && <div className="pipe-file-dropzone-error">{fileError}</div>}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.txt,.md,.xlsx,.xls,.png,.jpg,.jpeg"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {files.length > 0 && (
            <div className="pipe-file-list">
              {files.map((file, idx) => (
                <div key={idx} className="file-item pipe-file-item">
                  <div className="file-item-info">
                    <span className="file-item-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/></svg></span>
                    <span className="file-item-name" title={file.name}>{file.name}</span>
                  </div>
                  <div className="file-item-meta">{formatFileSize(file.size)}</div>
                  <Badge color="success">준비됨</Badge>
                  <button className="pipe-file-remove" onClick={() => removeFile(idx)}>✕</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Google Drive 탭 ── */}
      {activeTab === 'drive' && (
        <div className="pipe-drive-panel">
          {checkingStatus && (
            <div className="pipe-drive-loading">Google Drive 연결 상태 확인 중...</div>
          )}

          {/* 연결 안 됨 — 연결 설정 버튼 표시 */}
          {!checkingStatus && !driveConnected && (
            <div className="pipe-drive-not-connected">
              <div className="pipe-drive-not-connected-icon"><img src={googleDriveLogo} alt="" /></div>
              <div className="pipe-drive-not-connected-text">Google Drive 연결이 필요합니다</div>
              <div className="pipe-drive-not-connected-sub">
                Google Drive에서 파일을 가져오려면 먼저 연결 설정이 필요합니다.
              </div>
              <Button size='sm' className="pipe-drive-auth-btn" onClick={() => setShowConfigModal(true)}>
                연결 설정
              </Button>
            </div>
          )}

          {/* 연결됨 — 파일 선택 UI */}
          {!checkingStatus && driveConnected && (
            <>
              {/* 연결 계정 표시 */}
              <div className="kb-account-card">
                <div className="kb-account-info">
                  <span className="kb-account-icon">
                    <img src={googleDriveLogo} alt="" />
                  </span>
                  <div className="kb-account-text">
                    <strong>{driveAccount}</strong>
                    <span>Google Drive 계정이 연결되어 있습니다</span>
                  </div>
                </div>
                <div className="kb-account-actions">
                  <Button color="gray" appearance="outline" size="sm" onClick={() => setShowConfigModal(true)}>
                    <RefreshIcon /> 연결 설정
                  </Button>
                </div>
              </div>

              {/* 폴더 ID 입력 + 조회 버튼 */}
              <div className="pipe-drive-search-row">
                <input
                  type="text"
                  className="config-input pipe-drive-folder-input"
                  placeholder="폴더 URL 또는 ID (비우면 내 드라이브 전체)"
                  value={folderId}
                  onChange={(e) => setFolderId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadDriveFiles()}
                />
                <button
                  className="btn-primary pipe-drive-search-btn"
                  onClick={loadDriveFiles}
                  disabled={loadingFiles}
                >
                  {loadingFiles ? '조회 중...' : '파일 조회'}
                </button>
              </div>

              {driveError && (
                <Notice color="error">{driveError}</Notice>
              )}

              {/* 파일 목록 */}
              {driveFileList.length > 0 && (
                <div className="pipe-drive-file-list">
                  <div className="pipe-drive-file-list-header">
                    파일 선택 ({driveFiles.length}개 선택됨)
                  </div>
                  {driveFileList
                    .filter(f => !f.mimeType.includes('folder'))
                    .map(file => {
                      const selected = driveFiles.some(d => d.fileId === file.id);
                      return (
                        <div
                          key={file.id}
                          className={`pipe-drive-file-item${selected ? ' selected' : ''}`}
                          onClick={() => toggleDriveFile(file)}
                        >
                          <span className="pipe-drive-file-type"><img src={getDriveFileTypeIcon(file.mimeType)} alt="" /></span>
                          <div className="pipe-drive-file-info">
                            <div className="pipe-drive-file-name">{file.name}</div>
                            {file.modifiedTime && (
                              <div className="pipe-drive-file-meta">
                                수정: {new Date(file.modifiedTime).toLocaleDateString('ko-KR')}
                                {file.size ? ` · ${formatFileSize(file.size)}` : ''}
                              </div>
                            )}
                          </div>
                          <Checkbox checked={selected} readOnly aria-label={`${file.name} 선택`} />
                        </div>
                      );
                    })}
                </div>
              )}

              {driveFileList.length === 0 && !loadingFiles && !driveError && (
                <div className="pipe-drive-empty">
                  폴더 ID를 입력하거나 "파일 조회" 버튼을 눌러 Drive 파일을 불러오세요.<br />
                  <small style={{ color: 'var(--text-3)', fontSize: '11px' }}>
                    지원 형식: PDF · TXT · MD · Excel · 이미지(PNG/JPG)
                  </small>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Google Drive 설정 모달 */}
      <KbGoogleDriveConfigModal
        open={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        onConnected={handleConnected}
      />

      {/* ── Dropbox 탭 ── */}
      {activeTab === 'dropbox' && (
        <div className="pipe-drive-panel">
          {checkingDropbox && (
            <div className="pipe-drive-loading">Dropbox 연결 상태 확인 중...</div>
          )}
          {!checkingDropbox && !dropboxConnected && (
            <div className="pipe-drive-not-connected">
              <div className="pipe-drive-not-connected-icon"><img src={dropboxLogo} alt="" /></div>
              <div className="pipe-drive-not-connected-text">Dropbox 연결이 필요합니다</div>
              <div className="pipe-drive-not-connected-sub">
                Dropbox에서 파일을 가져오려면 먼저 연결 설정이 필요합니다.
              </div>
              <Button size="sm" className="pipe-drive-auth-btn" onClick={() => setShowDropboxConfigModal(true)}>
                연결 설정
              </Button>
            </div>
          )}
          {!checkingDropbox && dropboxConnected && (
            <>
              <div className="kb-account-card">
                <div className="kb-account-info">
                  <span className="kb-account-icon"><img src={dropboxLogo} alt="" /></span>
                  <div className="kb-account-text">
                    <strong>{dropboxAccount}</strong>
                    <span>Dropbox 계정이 연결되어 있습니다</span>
                  </div>
                </div>
                <div className="kb-account-actions">
                  <Button color="gray" appearance="outline" size="sm" onClick={() => setShowDropboxConfigModal(true)}>
                    <RefreshIcon /> 연결 설정
                  </Button>
                </div>
              </div>
              <div className="pipe-drive-search-row">
                <input
                  type="text"
                  className="config-input pipe-drive-folder-input"
                  placeholder="폴더 경로 (비우면 전체 Dropbox)"
                  value={dropboxFolderPath}
                  onChange={e => setDropboxFolderPath(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && loadDropboxFiles()}
                />
                <button
                  className="btn-primary pipe-drive-search-btn"
                  onClick={loadDropboxFiles}
                  disabled={loadingDropboxFiles}
                >
                  {loadingDropboxFiles ? '조회 중...' : '파일 조회'}
                </button>
              </div>
              {dropboxError && <Notice color="error">{dropboxError}</Notice>}
              {dropboxFileList.length > 0 && (
                <div className="pipe-drive-file-list">
                  <div className="pipe-drive-file-list-header">
                    파일 선택 ({dropboxFiles.length}개 선택됨)
                  </div>
                  {dropboxFileList
                    .filter(f => f['.tag'] === 'file')
                    .map(file => {
                      const selected = dropboxFiles.some(d => d.filePath === file.id);
                      return (
                        <div
                          key={file.path_lower}
                          className={`pipe-drive-file-item${selected ? ' selected' : ''}`}
                          onClick={() => {
                            if (!onDropboxFilesChange) return;
                            if (selected) {
                              onDropboxFilesChange(dropboxFiles.filter(d => d.filePath !== file.id));
                            } else {
                              // file.id는 이미 'id:XXXX' 형식
                              onDropboxFilesChange([...dropboxFiles, { filePath: file.id, fileName: file.name }]);
                            }
                          }}
                        >
                          <span className="pipe-drive-file-type">
                            <img src={DocumentIcon} alt="" />
                          </span>
                          <div className="pipe-drive-file-info">
                            <div className="pipe-drive-file-name">{file.name}</div>
                            {file.server_modified && (
                              <div className="pipe-drive-file-meta">
                                수정: {new Date(file.server_modified).toLocaleDateString('ko-KR')}
                                {file.size ? ` · ${formatFileSize(file.size)}` : ''}
                              </div>
                            )}
                          </div>
                          <Checkbox checked={selected} readOnly aria-label={`${file.name} 선택`} />
                        </div>
                      );
                    })}
                </div>
              )}
              {dropboxFileList.length === 0 && !loadingDropboxFiles && !dropboxError && (
                <div className="pipe-drive-empty">
                  폴더 경로를 입력하거나 "파일 조회" 버튼을 눌러 Dropbox 파일을 불러오세요.<br />
                  <small style={{ color: 'var(--text-3)', fontSize: '11px' }}>
                    지원 형식: PDF · TXT · MD · Excel · 이미지(PNG/JPG)
                  </small>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Dropbox 설정 모달 */}
      <KbDropboxConfigModal
        open={showDropboxConfigModal}
        onClose={() => setShowDropboxConfigModal(false)}
        onConnected={(email) => { setDropboxConnected(true); setDropboxAccount(email); }}
      />

    </div>
  );
}
